import { useEffect, useMemo } from 'react';

/**
 * Clamps a URL-backed page for display and snaps stale links — past the last
 * page, or page>1 on a confirmed empty result. Same behaviour as
 * {@link TasksTabPage} / {@link TitlesV3}, plus the empty-workspace case that
 * AppTableV2 would otherwise hide pagination for.
 *
 * `isSuccess` / `totalItems` are optional so callers that only need out-of-range
 * snap-back (or unit tests) keep the previous contract. Do not pass them until
 * the list query has settled, or a deep-linked page>1 would reset during load.
 */
export const usePaginationBounds = ({
  isSuccess,
  page,
  setPage,
  totalItems,
  totalPages,
}: {
  isSuccess?: boolean;
  page: number;
  setPage: (page: number) => void;
  totalItems?: null | number;
  totalPages: number;
}) => {
  const clampedPage = useMemo(
    () => Math.min(Math.max(page, 1), Math.max(totalPages, 1)),
    [page, totalPages],
  );

  const isEmptyPastPageOne =
    isSuccess === true && totalItems != null && totalItems === 0 && page > 1;
  const isPastLastPage = totalPages > 0 && page > totalPages;
  const isPageOutOfRange = isPastLastPage || isEmptyPastPageOne;

  useEffect(() => {
    if (isEmptyPastPageOne) {
      setPage(1);

      return;
    }

    if (isPastLastPage) setPage(totalPages);
  }, [isEmptyPastPageOne, isPastLastPage, setPage, totalPages]);

  return { clampedPage, isPageOutOfRange };
};
